import { useState } from 'react';
import { Modal, Avatar, Typography, Space, Tag, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWalletConnect } from '@/hooks/useWalletConnect';
import { useArmDelay } from '@/hooks/useArmDelay';
import { chainMetaByKey } from '@/lib/walletconnect/chains';
import { dappIdentityAllowsApproval, resolveDappIdentity } from '@/lib/walletconnect/verify';
import { DappIdentityNotice } from './DappIdentityNotice';

const { Text } = Typography;

/** First https icon from untrusted dApp metadata, if any. */
function dappIcon(meta: { icons?: unknown }): string | undefined {
  const icons: unknown = meta.icons;
  if (!Array.isArray(icons)) return undefined;
  const first: unknown = icons[0];
  return typeof first === 'string' && first.startsWith('https://') ? first : undefined;
}

/**
 * Shown when a dApp proposes a WalletConnect session. Confirms which vault (and
 * therefore which shared treasury address) will be exposed to the dApp before the
 * user approves. "Who is asking" is the WalletConnect Verify origin — the dApp's
 * self-declared name/url are shown only as a claim (ENTX-A-4).
 */
export function SessionProposalModal() {
  const { t } = useTranslation();
  const wc = useWalletConnect();
  const [acknowledged, setAcknowledged] = useState(false);

  const proposal = wc?.pendingProposal ?? null;
  const binding = wc?.pendingProposalBinding ?? null;
  const open = !!proposal && !!binding;
  const armed = useArmDelay(proposal ? String(proposal.id) : null);

  const meta = proposal?.params.proposer.metadata;
  const chainMeta = binding ? chainMetaByKey(binding.chain) : undefined;
  const identity = proposal ? resolveDappIdentity(proposal.verifyContext, meta) : null;
  const canApprove = !!identity && armed && dappIdentityAllowsApproval(identity, acknowledged);

  return (
    <Modal
      open={open}
      title={t('walletconnect.proposal.title')}
      okText={t('walletconnect.proposal.approve')}
      cancelText={t('walletconnect.proposal.reject')}
      onOk={() => {
        if (canApprove) void wc?.approveProposal();
      }}
      onCancel={() => void wc?.rejectProposal()}
      confirmLoading={wc?.connecting}
      okButtonProps={{ disabled: !canApprove }}
      destroyOnHidden
    >
      {meta && binding && identity && (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Space>
            {/* Self-declared icon: decorative only (dApp metadata is untrusted). */}
            <Avatar src={dappIcon(meta)} shape="square" size={40}>
              {identity.claimedName.charAt(0) || '?'}
            </Avatar>
            <DappIdentityNotice identity={identity} onAcknowledgedChange={setAcknowledged} />
          </Space>

          <Text type="secondary">{t('walletconnect.proposal.description')}</Text>

          <div>
            <Text type="secondary">{t('walletconnect.proposal.vault')}: </Text>
            <Text strong>{binding.vaultName}</Text> {chainMeta && <Tag>{chainMeta.name}</Tag>}
          </div>
          <div>
            <Text type="secondary">{t('walletconnect.proposal.address')}: </Text>
            <Text code copyable style={{ fontSize: 12 }}>
              {binding.address}
            </Text>
          </div>

          <Alert type="info" showIcon title={t('walletconnect.proposal.governance_note')} />
        </Space>
      )}
    </Modal>
  );
}
